import { useState } from 'react';
import { addRow, removeRowAt } from './listRowHelpers';

const makeSubjectTouched = () => false;
const makeSendConditionTouched = () => ({ sendCondition: false, day: false });

// Centralizes the display-only "revealed error" bookkeeping for the Basic Information form.
//
// A required error (and its red highlight) is shown for a field only once it has been
// *revealed* — and revealing happens exclusively on a save/publish attempt, which snapshots
// the fields that currently fail (see markVisible). Editing/adding/clearing a field between
// attempts therefore never surfaces a *new* error; an already-revealed error still clears
// live once the field is fixed. Blocking mirrors this via the reported state; the submit-time
// check still validates the whole form.
//
// Per-row state is kept with the SAME listRowHelpers as the form data (addRow / removeRowAt),
// so the arrays stay in lockstep with subjects / sendConditions and cannot drift out of sync.
export function useTouchedFields({ subjectCount, sendConditionCount }) {
  const [scalar, setScalar] = useState({
    templateName: false,
    campaign: false,
    trigger: false,
    tenant: false,
  });
  const [subjects, setSubjects] = useState(() =>
    Array.from({ length: subjectCount }, makeSubjectTouched),
  );
  const [sendConditions, setSendConditions] = useState(() =>
    Array.from({ length: sendConditionCount }, makeSendConditionTouched),
  );

  return {
    scalar,
    subjects,
    sendConditions,

    addSubject: () => setSubjects((prev) => addRow(prev, makeSubjectTouched)),
    removeSubject: (index) => setSubjects((prev) => removeRowAt(prev, index, makeSubjectTouched)),

    addSendCondition: () => setSendConditions((prev) => addRow(prev, makeSendConditionTouched)),
    removeSendCondition: (index) =>
      setSendConditions((prev) => removeRowAt(prev, index, makeSendConditionTouched)),

    // Trigger section leaving the screen (switch to Marketing): reset it to clean.
    resetTriggerSection: () => {
      setSendConditions([makeSendConditionTouched()]);
      setScalar((prev) => ({ ...prev, trigger: false }));
    },

    // A save/publish attempt snapshots which fields currently fail validation; only those are
    // flagged as revealed. Because non-failing fields carry no error to show anyway, this
    // produces the exact same display as "reveal everything" at the moment of the attempt —
    // the difference only shows up afterwards, when editing can no longer surface a new error.
    markVisible: ({
      scalar: nextScalar,
      subjects: nextSubjects,
      sendConditions: nextSendConditions,
    }) => {
      setScalar(nextScalar);
      setSubjects(nextSubjects);
      setSendConditions(nextSendConditions);
    },
  };
}
